import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginCredentials } from '@/types'
import { authApi } from '@/api/auth'
import { useGroupsStore } from '@/stores/groups'

const TOKEN_KEY = 'veripay_access_token'
const REFRESH_KEY = 'veripay_refresh_token'
const USER_KEY = 'veripay_user'
const PHONE_KEY = 'veripay_remember_phone'

export type LoginStep = 'idle' | 'pin' | 'otp' | 'set-pin' | 'forgot-pin' | 'forgot-pin-otp' | 'forgot-pin-set'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Multi-step login state
  const loginStep = ref<LoginStep>('idle')
  const loginPhone = ref('')
  const phoneMasked = ref('')
  const needsPinSetup = ref(false)
  const resetToken = ref('')

  // Computed
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  /** A platform operator signed in through `{phone}-ops`. Their permissions
   *  are already resolved against THIS sacco by the API — the flag only says
   *  the session is a support visit, which the banner and the router guard
   *  both need to know. */
  const isSupport = computed(() => user.value?.is_support === true)
  // An ops session carries a platform group (PLATFORM_OPS / SUPER_ADMIN), so
  // the portal gate has to read the session, not the group — otherwise the
  // guard would sign the operator straight back out.
  const isAdmin = computed(
    () => user.value?.role === 'SACCO_ADMIN' || user.value?.role === 'SUPER_ADMIN' || isSupport.value,
  )
  /** A forecourt partner — station staff, not the sacco's. They get their own
   *  shell (balances, payments, missed days, their own fills) and no part of
   *  the sacco's. */
  const isAttendant = computed(() => user.value?.role === 'FUEL_ATTENDANT')
  /** An attendant nobody has bound to a station yet. The binding is what
   *  scopes what they read and pins what they record, so until it exists the
   *  shell says so and the doors that need a forecourt stay shut. */
  const isUnboundAttendant = computed(
    () => isAttendant.value && !user.value?.fuel_station_id,
  )
  const stationName = computed(() => user.value?.fuel_station_name || null)

  /**
   * WHICH shell this session gets — the portal's one access decision.
   *
   *   full      — the sacco's own dashboard, sidebar and pages
   *   attendant — the fuel-only forecourt shell at /station
   *   none      — signed out with "access denied"
   *
   * Two shells, so it cannot be a boolean; deliberately NOT widened beyond
   * the groups named here. A MANAGER or CREW still has no web surface — that
   * is a product decision, not an oversight, and adding a group to a web
   * surface means giving it a shell, not relaxing a guard.
   */
  const portalAccess = computed<'full' | 'attendant' | 'none'>(() => {
    if (!user.value) return 'none'
    if (isAdmin.value) return 'full'
    if (isAttendant.value) return 'attendant'
    return 'none'
  })
  const userName = computed(() => user.value?.full_name || user.value?.phone || '')
  const rememberedPhone = computed(() => localStorage.getItem(PHONE_KEY) || '')

  // Initialize from localStorage
  function init() {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedRefresh = localStorage.getItem(REFRESH_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken) accessToken.value = storedToken
    if (storedRefresh) refreshToken.value = storedRefresh
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        // Invalid stored user
      }
    }

    // Refresh the user from /auth/me on page load when a token exists.
    // Without this, `user.permissions` is permanently the snapshot from the
    // last login — admin-side permission grants/revokes wouldn't propagate
    // until the user signed out and back in. Fired-and-forgotten so the app
    // keeps the locally-cached user visible while the network request runs.
    if (storedToken) {
      void fetchUser()
      void useGroupsStore().hydrate().catch(() => undefined)
    }
  }

  // Legacy password login (kept for backwards compatibility)
  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.login(credentials)

      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token
      user.value = response.user

      localStorage.setItem(TOKEN_KEY, response.access_token)
      localStorage.setItem(REFRESH_KEY, response.refresh_token)
      localStorage.setItem(USER_KEY, JSON.stringify(response.user))

      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed'
      return false
    } finally {
      loading.value = false
    }
  }

  // PIN+OTP login actions

  async function checkUser(phone: string) {
    loading.value = true
    error.value = null
    try {
      const result = await authApi.checkUser(phone)
      loginPhone.value = phone
      return result
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to check user'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loginWithPin(phone: string, pin: string) {
    loading.value = true
    error.value = null
    try {
      const result = await authApi.loginWithPin(phone, pin)
      phoneMasked.value = result.phone_masked
      loginStep.value = 'otp'
      return result
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Invalid PIN'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function verifyLoginOtp(phone: string, code: string) {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.verifyLoginOtp(phone, code)

      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token
      user.value = response.user

      localStorage.setItem(TOKEN_KEY, response.access_token)
      localStorage.setItem(REFRESH_KEY, response.refresh_token)
      localStorage.setItem(USER_KEY, JSON.stringify(response.user))

      void useGroupsStore().hydrate().catch(() => undefined)

      loginStep.value = 'idle'
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Invalid OTP'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function setupPin(phone: string, pin: string, confirmPin: string) {
    loading.value = true
    error.value = null
    try {
      await authApi.setupPin(phone, pin, confirmPin)
      loginStep.value = 'idle'
      needsPinSetup.value = false
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to set PIN'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forgotPin(phone: string) {
    loading.value = true
    error.value = null
    try {
      await authApi.forgotPin(phone)
      loginStep.value = 'forgot-pin-otp'
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to send OTP'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forgotPinVerify(phone: string, code: string) {
    loading.value = true
    error.value = null
    try {
      const result = await authApi.forgotPinVerify(phone, code)
      resetToken.value = result.reset_token
      loginStep.value = 'forgot-pin-set'
      return result
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Invalid OTP'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forgotPinSetPin(phone: string, pin: string, confirmPin: string) {
    loading.value = true
    error.value = null
    try {
      await authApi.forgotPinSetPin(phone, resetToken.value, pin, confirmPin)
      loginStep.value = 'idle'
      resetToken.value = ''
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to set PIN'
      throw err
    } finally {
      loading.value = false
    }
  }

  function rememberPhone(phone: string) {
    localStorage.setItem(PHONE_KEY, phone)
  }

  function clearRememberedPhone() {
    localStorage.removeItem(PHONE_KEY)
  }

  function resetLoginState() {
    loginStep.value = 'idle'
    loginPhone.value = ''
    phoneMasked.value = ''
    needsPinSetup.value = false
    resetToken.value = ''
    error.value = null
  }

  async function refresh() {
    if (!refreshToken.value) return false

    try {
      const response = await authApi.refresh(refreshToken.value)

      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token

      localStorage.setItem(TOKEN_KEY, response.access_token)
      localStorage.setItem(REFRESH_KEY, response.refresh_token)

      return true
    } catch {
      logout()
      return false
    }
  }

  async function fetchUser() {
    try {
      user.value = await authApi.getMe()
      localStorage.setItem(USER_KEY, JSON.stringify(user.value))
    } catch {
      // Ignore errors
    }
  }

  function logout() {
    // A support session has to be closed on the server too, not just here:
    // otherwise the sacco keeps being told support is in their account and the
    // access log shows a visit that never ended. Fire-and-forget with a dynamic
    // import (the api module pulls this store back in) — signing out locally
    // must not wait on it, and must happen even if it fails.
    if (user.value?.is_support && accessToken.value) {
      void import('@/api/support')
        .then((m) => m.supportApi.endSession())
        .catch(() => {})
    }

    user.value = null
    accessToken.value = null
    refreshToken.value = null

    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)

    useGroupsStore().reset()
    resetLoginState()
  }

  // Initialize on store creation
  init()

  return {
    user,
    accessToken,
    refreshToken,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isAttendant,
    isUnboundAttendant,
    stationName,
    portalAccess,
    isSupport,
    userName,
    loginStep,
    loginPhone,
    phoneMasked,
    needsPinSetup,
    rememberedPhone,
    login,
    checkUser,
    loginWithPin,
    verifyLoginOtp,
    setupPin,
    forgotPin,
    forgotPinVerify,
    forgotPinSetPin,
    rememberPhone,
    clearRememberedPhone,
    resetLoginState,
    refresh,
    fetchUser,
    logout,
  }
})
